import type {WixImageInfo} from '@wix/devzai-common-wix';
import type {Values} from '@wix/devzai-utils-common';

/**
 * Registration type for events.
 * Indicates whether the event is a ticketed event or an RSVP event.
 */
export const DoppeDtoEventRegistrationType = {
    /**
     * Registration type is unknown or not available.
     */
    Unknown: 'Unknown',
    /**
     * Event requires RSVP registration.
     */
    RSVP: 'RSVP',
    /**
     * Event requires ticket purchase for registration.
     */
    Ticketing: 'Ticketing',
} as const;

export type DoppeDtoEventRegistrationType = Values<typeof DoppeDtoEventRegistrationType>;

export interface DoppeDtoEvent {
    id: string;
    name: string;
    description?: string;
    media: WixImageInfo[] | null;
    eventDates: {
        eventStartDate: string | null;
        eventEndDate: string | null;
        formattedEventDates: string;
    };
    eventPageUrl?: string;
    createdDate: Date | null;
    highestPrice: string | null;
    lowestPrice: string | null;
    location: string | null;
    slug: string;
    /**
     * The registration type of the event (RSVP or Ticketing).
     * Determines whether the event shows "Buy Tickets" or "RSVP" button.
     */
    registrationType: DoppeDtoEventRegistrationType;
}

export namespace DoppeDtoEvent {
    export type Id = DoppeDtoEvent['id'];
}

/**
 * Options for creating a dummy event with localized content and images.
 */
export interface DoppeDtoEventCreateDummyOptions {
    /**
     * Localized event name.
     */
    name: string;
    /**
     * Localized event description.
     */
    description: string;
    /**
     * Localized "Date TBD" text.
     */
    dateTBD: string;
    /**
     * Event image (WixImageInfo format).
     */
    image: WixImageInfo | null;
}

/**
 * Creates a dummy event with placeholder content.
 * Used when no actual event is available (e.g., for preview or empty state).
 */
export function doppeDtoEventCreateDummy(
    options: DoppeDtoEventCreateDummyOptions
): DoppeDtoEvent {
    return {
        id: 'dummy-event',
        name: options.name,
        description: options.description,
        media: options.image ? [options.image] : null,
        eventDates: {
            eventStartDate: null,
            eventEndDate: null,
            formattedEventDates: options.dateTBD
        },
        createdDate: null,
        highestPrice: null,
        lowestPrice: null,
        location: null,
        slug: 'dummy-event',
        registrationType: DoppeDtoEventRegistrationType.Ticketing,
    };
}

